import { Form } from '@epages/react-components'
import type { ReactElement } from 'react'
import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { kebabCase } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import Immutable from 'immutable'
import cc from 'classcat'

import { formatHref } from '../templateComponents/Workspace/utils/link-sanitizer'
import {
  previewFooterPages,
  previewLegalPages,
  previewUserSettings,
  setUserSettings,
  updateFooterNavigation,
  updateLegalNavigation,
  updateLegalSettings,
  updatePageSettings,
} from '../../store/actions'
import { useTranslate } from '../../utils/translate'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import FooterCopyrightSettings from './FooterCopyrightSettings'
import FooterLayoutSettings from './FooterLayoutSettings'
import FooterSectionsSettings from './FooterSectionsSettings'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'

// Beware: Key is also used as translation key and content access key
const tabs = {
  layout: FooterLayoutSettings,
  sections: FooterSectionsSettings,
  copyright: FooterCopyrightSettings,
} as Record<string, any>

type Props = {
  active: boolean | string
  onToggle: ({ submit }?: { submit: boolean }) => void
}

export type DefaultValueParams = {
  userSettings: ImmutableMap
  shopLocale: string
  footerPages: ImmutableList
  shopResellerName: string
  taxCountry: string
  t: (key: string) => string
}

// Used to read the default value from each tab component's static field.
// If the static `defaultValue` is a function, it'll be executed with an
// object containing properties ("userSettings", "shopLocale", "footerPages", "shopResellerName", "taxCountry", "t"),
// allowing the component to return locale and/or reseller specific values.
export function getDefaultValue(params: DefaultValueParams, [key, { defaultValue, displayName }]) {
  if (!defaultValue && process.env.NODE_ENV === 'development')
    /* istanbul ignore next */
    throw new Error(displayName + ' does not have a defaultValue')

  return Immutable.Map({
    [key]: typeof defaultValue === 'function' ? defaultValue(params) : defaultValue,
  })
}

export default function FooterSettings({ active, onToggle }: Readonly<Props>): ReactElement {
  const [activeTab, setActiveTab] = useState(typeof active === 'string' && tabs[active] ? active : Object.keys(tabs)[0])
  const isDeepLinkToSocialMediaPopover = active === 'socialNetworks'

  const dispatch = useDispatch()

  const footerPages = useSelector<State, ImmutableList>((state) => state.get('footerPages'))
  const legalPages = useSelector<State, ImmutableList>((state) => state.get('legalPages'))

  const shopLocale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const taxCountry = useSelector<State, string>((state) => state.getIn(['tax', 'country']))
  const shopResellerName = useSelector<State, string>((state) => state.get('resellerName'))
  const userSettings = useSelector<State, ImmutableMap>((state) =>
    state.getIn(['shop', 'userSettings']).delete('googleAnalytics'),
  )
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))

  const initialUserSettings = useRef(userSettings)
  const initialFooterPages = useRef(footerPages)
  const initialLegalPages = useRef(legalPages)

  // Resets any unsaved local changes (changes that are not affecting data that is also used in other bottom
  // bar setting layers). Shared bottom bar settings changes are reset in the parent `BottomBar` component.
  const resetUnsavedLocalChanges = useCallback(() => {
    dispatch(
      previewFooterPages(
        initialFooterPages.current
          .map((page: ImmutableMap) => {
            const currentPage = initialFooterPages.current.find((p: ImmutableMap) => p.get('id') === page.get('id'))

            // Reset only the pages that are in preview mode.
            return currentPage.get('isPreview') ? page : currentPage
          })
          .toList(),
      ),
    )
    dispatch(previewLegalPages(initialLegalPages.current))
  }, [dispatch])

  // Remember initial values when opening the settings,
  // and reset any unsaved local changes on close.
  useEffect(() => {
    if (active) {
      initialUserSettings.current = userSettings
      initialFooterPages.current = footerPages
      initialLegalPages.current = legalPages
    }

    return () => {
      if (active) resetUnsavedLocalChanges()
    }
  }, [active, resetUnsavedLocalChanges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update the initial footer pages ref when a footer page is added or removed.
  useEffect(() => {
    if (footerPages.size > initialFooterPages.current.size) {
      initialFooterPages.current = initialFooterPages.current.push(footerPages.get(-1))
    } else if (footerPages.size < initialFooterPages.current.size) {
      const removedIndex = initialFooterPages.current.findIndex((page: ImmutableMap) =>
        footerPages.every((p: ImmutableMap) => p.get('id') !== page.get('id')),
      )
      initialFooterPages.current = initialFooterPages.current.delete(removedIndex)
    }
  }, [footerPages])

  // Activate given tab in case of deep linking to it.
  useEffect(() => {
    if (typeof active === 'string') setActiveTab(isDeepLinkToSocialMediaPopover ? 'sections' : active)
  }, [active, isDeepLinkToSocialMediaPopover])

  const scopedT = useTranslate('interface', 'components.bottomBar.footerSettings')
  const scopedShopT = useTranslate('shop', 'components.bottomBar.footerSettings')

  const params = { userSettings, shopLocale, footerPages, shopResellerName, taxCountry, t: scopedShopT }
  // tab components expose a JS object of the form { [settingName: string]: Immutable.Map }
  const tabValueDefaults: ImmutableMap[] = Object.values<any>(tabs)
    .reduce((defaults, tab) => {
      return defaults.mergeDeep(getDefaultValue(params, ['key', tab]).get('key'))
    }, Immutable.Map())
    .entrySeq()
    .map(([key, value]) => Immutable.Map([[key, value]]))
    .toArray()

  async function handleSubmit(data: ImmutableMap) {
    const userSettingsData = data.delete('footerPages').delete('legalPages')
    const footerPagesData = data.get('footerPages')
    const legalPagesData = data.get('legalPages')

    const sanitizedUserSettingsData = userSettingsData.updateIn(['socialNetworks'], (networks: ImmutableMap) => {
      return networks.map((value: string, key: string) => {
        if (key.endsWith('Url') && value) {
          const formattedLink = formatHref(value)
          return formattedLink
        }
        return value
      })
    })
    dispatch(setUserSettings(sanitizedUserSettingsData.toJS()))

    await dispatch(updateFooterNavigation(footerPagesData))
    footerPagesData
      .filter((footerPage: ImmutableMap) => {
        const oldPage = initialFooterPages.current.find((page: ImmutableMap) => page.get('id') === footerPage.get('id'))
        return oldPage.get('isVisible') !== footerPage.get('isVisible')
      })
      .forEach(async (footerPage: ImmutableMap) => {
        await dispatch(
          updatePageSettings(
            footerPage.set('oldPageSlug', footerPage.get('slug')).set('newPageSlug', footerPage.get('slug')).toJS(),
          ),
        )
      })

    dispatch(updateLegalNavigation(legalPagesData))
    legalPagesData
      .filter((legalPage: ImmutableMap) => {
        const oldPage = initialLegalPages.current.find((page: ImmutableMap) => page.get('id') === legalPage.get('id'))
        return oldPage.get('isVisible') !== legalPage.get('isVisible')
      })
      .forEach((legalPage: ImmutableMap) => dispatch(updateLegalSettings(legalPage.toJS())))

    // Auto-checks the "Set up your social media links in the footer" to-do in the BYD cockpit's dashboard checklist and setup guide
    if (!userSettingsData.get('socialNetworks').equals(initialUserSettings.current.get('socialNetworks'))) {
      window.parent?.postMessage(
        { type: 'CHECKLIST', payload: { websiteOnly: 'contentSection.socialMedia' } },
        mboBaseUrl,
      )
    }

    initialUserSettings.current = userSettingsData
    initialFooterPages.current = footerPagesData
    initialLegalPages.current = legalPagesData

    onToggle({ submit: true })
  }

  function handleCancel() {
    resetUnsavedLocalChanges()
    onToggle()
  }

  function prepareFormValue(formValue: ImmutableMap) {
    const data = formValue.mergeDeep(...tabValueDefaults, formValue)

    // Handle correct merge of the logo collections (payment and shipping logo settings).
    // Immutable.mergeDeep can’t know how to merge two lists of Immutable.Map’s, the default list
    // and the user-saved list.
    //
    // The provided logos are hard coded:
    // When the hard coded logo list gets updated, e.g. new logos added to the middle of the list,
    // then (without this handling) logos can appear twice in the editor settings logo list in a
    // shop with previously saved settings. The doubled entries can then also both be activated
    // and also saved to the database.
    //
    // To correctly merge, build the logo settings list for the editor form from the default
    // value list. This also ensures the correct order according to the default list.

    return tabValueDefaults.reduce((data, tabValueDefault) => {
      // key = footerPages | paymentLogos | shippingLogos | socialNetworks | footerLayout | copyright
      // defaultValue = the default value for the key
      const [key, defaultValue] = tabValueDefault.entrySeq().first()

      // Value is the current form value for the key
      let value = data.get(key)

      // Check for specific logo collections data structure (see payment and shipping settings).
      if (
        Immutable.List.isList(defaultValue) &&
        Immutable.Map.isMap(defaultValue.first()) &&
        defaultValue.first().has('id')
      ) {
        // Set value to the default logo settings list merged with any user-saved values,
        // also preserving custom logos.
        const customLogos = value.filter((item) => item.has('path'))
        value = defaultValue
          .map((defaultLogoSetting) => {
            // Handle regression in database:
            // The user-saved logo setting could appear twice with the same logo setting
            // ID in the database. Retain the first one whose "active" prop value differs
            // from the default (which is always `false` at the moment). If nothing is
            // found, then the default setting will be used.
            const userLogoSetting = formValue
              .get(key)
              .find(
                (logoSetting) =>
                  logoSetting.get('id') === defaultLogoSetting.get('id') &&
                  logoSetting.get('active') !== defaultLogoSetting.get('active'),
              )

            return defaultLogoSetting.merge(userLogoSetting)
          })
          .concat(customLogos)
      }

      return data.set(key, value)
    }, data)
  }

  return (
    <>
      <button
        className={cc(['ep-bottom-bar-button', 'ep-bottom-bar-button-footer-settings', { checked: active }])}
        title={scopedT('title')}
        onClick={() => onToggle()}
      />
      <SettingsLayer
        className={cc(['ep-settingslayer-footer-settings', `${kebabCase(activeTab)}-active`])}
        onEscapeKeyDown={handleCancel}
        open={!!active}
        side="top"
        align="center"
      >
        {({ renderLayout }) => (
          <Form
            name="userSettings"
            value={userSettings.set('footerPages', footerPages).set('legalPages', legalPages)}
            prepare={prepareFormValue}
            onChange={(data: ImmutableMap) => {
              requestAnimationFrame(() => {
                dispatch(previewUserSettings(data.delete('footerPages').delete('legalPages')))
                dispatch(previewFooterPages(data.get('footerPages')))
                dispatch(previewLegalPages(data.get('legalPages')))
              })

              // Make sure not to return any data in order to prevent 'smart' data handling
              // https://github.com/ePages-de/react-components/blob/v0.4.3/src/form/Form.jsx#L142-L143
              return null
            }}
            onSubmit={handleSubmit}
          >
            {renderLayout({
              title: scopedT(`${activeTab}.title`),
              tabs: Object.keys(tabs).map((tab) => ({
                name: tab,
                title: scopedT(`${tab}.title`),
                active: activeTab === tab,
                onClick: (name: string) => setActiveTab(name),
              })),
              body: createElement(tabs[activeTab], {
                showSocialMedia: isDeepLinkToSocialMediaPopover,
              }),
              footer: (
                <ButtonRow>
                  <Button type="button" variant="cancel" onClick={handleCancel}>
                    {scopedT('cancelButton.label')}
                  </Button>
                  <Button type="submit">{scopedT('applyButton.label')}</Button>
                </ButtonRow>
              ),
            })}
          </Form>
        )}
      </SettingsLayer>
    </>
  )
}
